/**
 * @module varianceanalysis/store/gllist
 * @description Vuex store module for GL List management
 * 
 * Features:
 * - Delta-based order saving (optimized)
 * - Legacy full-save support (backward compatibility)
 * - Efficient state management
 */

const state = {
  glList: [],
  originalSnapshot: null, // Snapshot for delta tracking
  isSaving: false,
};

const getters = {
  glList: (state) => state.glList,
  originalSnapshot: (state) => state.originalSnapshot,
  isSaving: (state) => state.isSaving,
};

const mutations = {
  SET_GL_LIST(state, payload) {
    state.glList = payload;
  },
  SET_ORIGINAL_SNAPSHOT(state, payload) {
    state.originalSnapshot = payload;
  },
  SET_IS_SAVING(state, payload) {
    state.isSaving = payload;
  },
  CLEAR_GL_STATE(state) {
    state.glList = [];
    state.originalSnapshot = null;
    state.isSaving = false;
  },
};

const actions = {
  /**
   * Fetches the GL list from the server
   * @param {Object} context - Vuex context
   * @param {Object} axiosInstance - Axios instance
   * @returns {Promise<Array>} GL list data
   */
  async getGLList({ commit }, axiosInstance) {
    try {
      const response = await axiosInstance.post("/v2/varianceanalysis/gllist/get");
      if (response.data.status === "success") {
        commit("SET_GL_LIST", response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Error reading data");
      }
    } catch (error) {
      console.error("[GLStore]: getGLList error", error);
      commit("sbError", error.response?.data?.message || error.message || "Failed to fetch GL List", {
        root: true,
      });
      throw error;
    }
  },

  /**
   * Legacy: Saves the full GL order (backward compatibility)
   * @param {Object} context - Vuex context
   * @param {Object} payload - { data, axiosInstance }
   * @returns {Promise<boolean>} Success status
   */
  async saveGLOrder({ commit }, { data, axiosInstance }) {
    try {
      commit("SET_IS_SAVING", true);
      const response = await axiosInstance.post("/v2/varianceanalysis/gllist/saveorder", {
        data: data,
      });
      if (response.data.status === "success") {
        commit("sbSuccess", "GL order saved", { root: true });
        return true;
      } else {
        throw new Error(response.data.message || "Error updating gl order");
      }
    } catch (error) {
      console.error("[GLStore]: saveGLOrder error", error);
      commit("sbError", error.response?.data?.message || error.message || "Failed to save GL Order", {
        root: true,
      });
      throw error;
    } finally {
      commit("SET_IS_SAVING", false);
    }
  },

  /**
   * NEW: Saves GL order changes using delta-based approach
   * Only sends modified records to the server
   * 
   * @param {Object} context - Vuex context
   * @param {Object} payload - { changes, axiosInstance }
   * @param {Array} payload.changes - Array of { code, order, parent }
   * @param {Object} payload.axiosInstance - Axios instance
   * @returns {Promise<Object>} Result with matchedCount, modifiedCount
   */
  async saveGLOrderDelta({ commit }, { changes, axiosInstance }) {
    try {
      commit("SET_IS_SAVING", true);

      // Early return if no changes
      if (!changes || changes.length === 0) {
        commit("sbInfo", "No changes to save", { root: true });
        return { matchedCount: 0, modifiedCount: 0 };
      }

      const response = await axiosInstance.post("/v2/varianceanalysis/gllist/v2/save-order-delta", {
        changes: changes,
      });

      if (response.data.status === "success") {
        const result = response.data.data || {};
        commit("sbSuccess", `GL order saved (${result.modifiedCount || 0} changes)`, { root: true });
        return result;
      } else {
        throw new Error(response.data.message || "Error updating gl order");
      }
    } catch (error) {
      console.error("[GLStore]: saveGLOrderDelta error", error);
      
      // Handle validation errors from server
      if (error.response?.status === 400) {
        const errorData = error.response.data;
        const message = errorData.message || "Validation error";
        commit("sbError", message, { root: true });
      } else {
        commit("sbError", error.response?.data?.message || error.message || "Failed to save GL Order", {
          root: true,
        });
      }
      throw error;
    } finally {
      commit("SET_IS_SAVING", false);
    }
  },

  /**
   * Sets the original snapshot for delta tracking
   * Call this after loading data to enable change detection
   * 
   * @param {Object} context - Vuex context
   * @param {Map} snapshot - Snapshot map from glListUtils.createSnapshot()
   */
  setOriginalSnapshot({ commit }, snapshot) {
    commit("SET_ORIGINAL_SNAPSHOT", snapshot);
  },

  /**
   * Clears all GL state (call on component destroy)
   * @param {Object} context - Vuex context
   */
  clearGLState({ commit }) {
    commit("CLEAR_GL_STATE");
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

